import { PiggyBankIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasPercentageInput,
  CanvasSimpleSelect,
  PercentOperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import {
  modelProbabilityExplanation,
  NodeDocs,
} from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  SpamAnalysisShape,
  spamAnalysisType,
  comparisonOperators,
  ComparisonOperator,
  SpamAnalysisProps,
  spamAnalysisCategories,
  SpamAnalysisCategory,
  metadataType,
  Dimensions,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";

export function isSpamAnalysisShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is SpamAnalysisShape {
  return shape.type === spamAnalysisType;
}

export class SpamAnalysisTool extends ToolStateNode {
  static override id = spamAnalysisType;

  static override uiToolDef = (editor: Editor) => ({
    id: spamAnalysisType,
    icon: PiggyBankIcon,
    label: SpamAnalysisShapeUtil.docs.title,
    defaultColor: SpamAnalysisShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(spamAnalysisType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={SpamAnalysis}
        util={SpamAnalysisShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: spamAnalysisType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class SpamAnalysisShapeUtil extends BindableUtil<SpamAnalysisShape> {
  static override migrations = createMigrations(spamAnalysisType);
  static override type = spamAnalysisType;

  static override docs = {
    defaultColor: "pink" as const,
    title: "Spam Analysis",
    description: "Use AI to analyze if this post is spammy.",
  };

  override getType() {
    return spamAnalysisType;
  }

  static override props: RecordProps<SpamAnalysisShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    spamAnalysisCategory: T.literalEnum(...spamAnalysisCategories),
    operator: T.literalEnum(...comparisonOperators),
    value: T.or(T.number, T.string),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 440,
      minW: 440,
      h: MIN_H,
      spamAnalysisCategory: "Marketing Spam" as const,
      operator: ">=" as const,
      value: 0.5,
      metadata: undefined,
    };
  }

  override component(shape: SpamAnalysisShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <SpamAnalysis
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function SpamAnalysis({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: SpamAnalysisShape;
  className?: string;
  updateShape: (
    shape: SpamAnalysisShape,
    props: Partial<SpamAnalysisProps>,
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={
          shape.props.metadata?.color || SpamAnalysisShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={SpamAnalysisShapeUtil.docs}
        />
        <BlockBody>
          <CanvasSimpleSelect
            aria-label="Spam Analysis Category"
            options={spamAnalysisCategories}
            value={shape.props.spamAnalysisCategory}
            className="shrink-0"
            onValueChange={(v) =>
              updateShape(shape, {
                spamAnalysisCategory: v as SpamAnalysisCategory,
              })
            }
            tooltip={SpamAnalysisShapeUtil.docs.description}
          />
          <PercentOperatorSelect
            value={shape.props.operator}
            onValueChange={(v) =>
              updateShape(shape, { operator: v as ComparisonOperator })
            }
          />
          <SimpleTooltip message={modelProbabilityExplanation}>
            <CanvasPercentageInput
              aria-label="Threshold"
              value={shape.props.value as number}
              className="flex-1"
              placeholder="Threshold"
              onValueChange={(value) => updateShape(shape, { value })}
            />
          </SimpleTooltip>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
